import { ReactNode, useEffect, useState } from "react";
import styles from "./style.module.scss";

import PageContainer from "@components/layout/PageContainer";
import DynamicForm, { makeFormInputsUneditable } from "@mavichpe/dynamic-forms";
import { PriceManagementFormData } from "@/formTypes/PriceManagementDataType";
import PriceManagementFormConfig from "./form";
import { fetchSuscription, SuscriptionDataType, updateSuscription } from "@/api/SuscriptionsApi";
import { showAlert } from "@utils/Alert";
import { useNavigate } from "react-router-dom";
import { Col, Row, Table } from "reactstrap";
import { PriceApi, PriceDataType } from "@/api/PricesApi";
import { renderItemActions } from "@utils/ListUtils";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import useFeatureAccess from "@/hooks/useFeatureAccess";
import { useAdminContext } from "@/hooks/useAdminContext";

const PriceManagement = () => {
  const navigate = useNavigate();
  const { hasFeature } = useFeatureAccess();
  const { adminSuscriptionId, isAdminMode, profileBackUrl, adminQuerySuffix } = useAdminContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [suscription, setSuscription] = useState<SuscriptionDataType>({});
  const [prices, setPrices] = useState<PriceDataType[]>([]);

  const canChangePriceManaged = isAdminMode || hasFeature("action:pricemanaged:change");

  useEffect(() => {
    const loadData = async () => {
      try {
        const suscriptionId = isAdminMode ? adminSuscriptionId : undefined;
        const data = await fetchSuscription(suscriptionId ?? undefined);
        setSuscription(data);

        await fetchPrices()
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    loadData();
  }, []);

  const fetchPrices = async () => {
    const pricesData = await PriceApi.fetchAll();
    setPrices(pricesData.items);
  }

  const listActions = [
    {
      href: `/user/price/{:id}${adminQuerySuffix}`,
      actionTitle: "Editar Precio",
      iconProps: { icon: faEdit }
    },
    {
      actionTitle: "Eliminar Precio",
      iconProps: { icon: faTrash },
      needConfirm: true,
      confirmProps: {
        title: "Eliminar Precio",
        confirmMessage: "¿Seguro que desea eliminar este Precio?",
        confirmBtnText: "Eliminar"
      },
      onClick: (id: string) => { deleteRecord(id) }
    }
  ]

  const deleteRecord = async (id: string) => {
    setIsLoading(true);
    try {
      const { errors } = await PriceApi.delete(id);
      if (errors?.length) {
        showAlert('danger', 'El Precio no puedo ser eliminado');
      } else {
        showAlert('success', 'El Precio fue eliminado correctamente');
      }
      await fetchPrices()
    } finally {
      setIsLoading(false);
    }
  }

  const updateProfile = async (name: keyof PriceManagementFormData, value: any) => {
    if (!canChangePriceManaged) {
      showAlert("warning", "Tu plan actual no permite cambiar el tipo de manejo de precios");
      return;
    }
    setIsLoading(true);
    try {
      if (suscription?.id) {
        await updateSuscription(suscription.id, { [name]: value });
        setSuscription({
          ...suscription,
          [name]: value
        })
        showAlert("success", "Informacion actualizada")
      }
    } finally {
      setIsLoading(false);
    }
  }

  const isCalculatedPrice = suscription?.pricemanaged == "2";

  const getFormConfig = () => {
    if (!canChangePriceManaged) {
      return makeFormInputsUneditable<PriceManagementFormData>(PriceManagementFormConfig, ["pricemanaged"]);
    }
    return PriceManagementFormConfig;
  }

  const primaryActions = isCalculatedPrice ? [
    {
      onClick: () => {
        navigate(`/user/price${adminQuerySuffix}`)
      },
      actionTitle: "Nuevo Precio",
      color: "success"
    }
  ] : []

  const renderPriceTable = (): ReactNode[] => {
    return prices.map(p => {
      return (
        <tr>
          <td>{p.number}</td>
          <td>{p.title}</td>
          <td>{p.formula}</td>
          <td className={p.default ? styles.greenBg : null}>{p.default ? "Si" : "No"}</td>
          <td>
            <div className={styles.actions}>
              {renderItemActions(p, listActions)}
            </div>
          </td>
        </tr>
      )
    })
  }

  return (
    <PageContainer
      title="Manejo de Precios"
      backUrl={profileBackUrl}
      primaryActions={primaryActions}
    >
      <h3>Manejo de Precios de Productos</h3>
      <p>Los precios en <b>EasyPOS</b> se pueden manejar de 2 formas:</p>
      <ul className={styles.optionsList}>
        <li>Manejar 4 Precios fijos por producto</li>
        <li>Calcular el precio de los productos segundo las formulas que usted desee utilizar tomando en cuenta el costo de los productos</li>
      </ul>

      {!canChangePriceManaged && (
        <p className={styles.featureNotice}>
          Tu plan actual no permite cambiar el tipo de manejo de precios. Actualiza tu plan para habilitar esta función.
        </p>
      )}

      <DynamicForm<PriceManagementFormData>
        externalFormState={suscription}
        formConfig={getFormConfig()}
        isLoading={isLoading}
        onInputChange={updateProfile}
      />

      {isCalculatedPrice && (<Row className="mt-3">
        <Col md={{ size: 10, offset: 1 }}>
          <Table bordered>
            <thead>
              <tr>
                <th># Precio</th>
                <th>Nombre</th>
                <th>Formula</th>
                <th>Por Defecto</th>
                <th>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {renderPriceTable()}
            </tbody>
          </Table>
        </Col>
      </Row>
      )
      }

    </PageContainer>
  );
}

export default PriceManagement;
